/**
 * Phase 2B.3 — Path-based tenant slug detection.
 *
 * Reads the first path segment off `window.location.pathname`. If it's a
 * valid agent slug (kebab-case, 3–32 chars, not on the reserved list),
 * returns it. Otherwise returns null.
 *
 * Used by:
 *   • BrowserRouter `basename` so e.g. `/sarah-bennett/login` works as `/login`
 *     for all React Router routes.
 *   • ContentProvider's unauthenticated `/api/content?agent_slug=…` fetch so
 *     the login screen can show that agent's brand BEFORE sign-in.
 *
 * Server-side reserved list is the source of truth (see `_RESERVED_SLUGS` in
 * server.py); we mirror it here to short-circuit the lookup for known
 * non-tenant first segments without an extra round-trip.
 */
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MIN_LEN = 3;
const MAX_LEN = 32;

const RESERVED = new Set([
  // Top-level app routes (must NEVER be confused with a tenant slug)
  "admin", "api", "app", "auth", "for-agents", "signup", "login", "register",
  "logout", "pricing", "contact", "about", "blog", "help", "docs", "support",
  "terms", "privacy", "settings", "billing", "marketing", "static", "public",
  "preview", "porchlight", "porch-light", "sellwitherika", "buyerhub", "buyer",
  // Seller-side routes
  "dashboard", "prep-guide", "process", "forms", "net-sheet", "dates",
  "vendors", "closing", "documents", "ask", "invite", "forgot", "reset",
  // Static assets
  "favicon.ico", "robots.txt", "manifest.json", "service-worker.js",
]);

export function getTenantSlug() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "/";
  const first = path.split("/").filter(Boolean)[0] || "";
  if (!first) return null;
  if (RESERVED.has(first)) return null;
  if (first.length < MIN_LEN || first.length > MAX_LEN) return null;
  if (!SLUG_RE.test(first)) return null;
  return first;
}
